import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import { InvoiceTermsEditor } from "@/components/offer/InvoiceTermsEditor";
import { useUpsells } from "@/hooks/use-upsells";
import { useInvoiceCalculations } from "@/hooks/use-invoice-calculations";
import { useUpdateInvoicePlan } from "@/hooks/use-invoice-plan";
import { formatNOK } from "@/lib/format";
import {
  computeTermLocks,
  isTermLocked,
  lockedPct,
  pctToAmount,
  roundPct,
  validateInvoicePlanEdit,
  type InvoiceTerm,
} from "@/lib/invoice-plan";
import type { DBInvoice, DBProject } from "@/lib/db-types";

interface InvoicePlanCardProps {
  project: DBProject;
  invoices: DBInvoice[];
  /** VIB-722c: edit_invoice_terms. */
  canEdit: boolean;
}

/**
 * VIB-2836: faktureringsplanen på Detaljer. Samme intervall-editor som i
 * tilbudet; ledd som allerede er fakturert er låst, og leddene som gjenstår må
 * gå opp i det som er igjen av planen. Endringen logges av DB-triggeren
 * (gammel og ny plan, aktør).
 */
export function InvoicePlanCard({ project: p, invoices, canEdit }: InvoicePlanCardProps) {
  const { data: upsells } = useUpsells(p.id);
  const calc = useInvoiceCalculations(p, invoices, upsells ?? []);
  const locks = useMemo(() => computeTermLocks(invoices), [invoices]);
  const updatePlan = useUpdateInvoicePlan();
  const [draft, setDraft] = useState<InvoiceTerm[] | null>(null);

  const current = calc.planTerms;
  const editable = canEdit && !locks.finalInvoiced;
  const remainingPct = roundPct(100 - lockedPct(current, locks));
  // Låsene følger den lagrede planen: et ledd lagt til i utkastet er aldri fakturert.
  const lockedCount = locks.finalInvoiced ? current.length : Math.min(locks.lockedPrefix, current.length - 1);
  const validation = draft ? validateInvoicePlanEdit(current, draft, locks) : null;
  const changed = draft != null && JSON.stringify(draft) !== JSON.stringify(current);

  const save = () => {
    if (!draft || validation) return;
    updatePlan.mutate(
      { projectId: p.id, terms: draft, expectedUpdatedAt: p.updated_at },
      { onSuccess: () => setDraft(null) },
    );
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Faktureringsplan</CardTitle>
        {editable && !draft && (
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setDraft(current)}>
            Endre plan
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-xs text-muted-foreground">
          Gjenstår av kontrakten: {formatNOK(calc.remainingContract)} ({remainingPct} % av planen).
          Sluttfakturaen tar det som gjenstår.
        </p>

        {draft ? (
          <>
            <InvoiceTermsEditor
              terms={draft}
              onChange={setDraft}
              isLocked={(i) => i < lockedCount}
              amountBase={calc.contractAmount}
              validationMessage={validation}
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setDraft(null)} disabled={updatePlan.isPending}>
                Avbryt
              </Button>
              <Button size="sm" onClick={save} disabled={!changed || validation != null || updatePlan.isPending}>
                {updatePlan.isPending && <SrIcon name="loader2" className="w-3 h-3 animate-spin mr-1" />}
                Lagre plan
              </Button>
            </div>
          </>
        ) : (
          <ul className="space-y-1.5">
            {current.map((term, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className="flex-1">{term.label}</span>
                <span className="tabular-nums text-muted-foreground">{term.pct} %</span>
                <span className="w-24 text-right tabular-nums">
                  {formatNOK(pctToAmount(term.pct, calc.contractAmount))}
                </span>
                <span className="w-4" aria-label={isTermLocked(locks, i, current.length) ? "Fakturert ledd" : undefined}>
                  {isTermLocked(locks, i, current.length) && (
                    <SrIcon name="lock" className="w-3.5 h-3.5 text-muted-foreground" />
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
